import React, { useEffect, useState } from 'react';
import styled from "@emotion/styled";
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { CustomerInterface, RootState, i18n, mediaQuery, openCallMeModal, setBListToggle, setCustomer, setFlowType, setOneTimeOffersToggle, setProgressBarPercentage, useAppDispatch, useAppSelector, useHealthQuery } from 'src/common';
import { Divider, FilterCard, Flex, Loader, OfferCard, RequestAgentCard, SendOffersToEmailCard, Text } from 'src/components';
import { AppRoutes } from '../_router/app.routes';

const FlexStyle = styled(Flex)({
  gap: '5px',

  [mediaQuery('mobile')]: {
    gap: '10px'
  },
})

const cleanBase64String = (str: string) =>
  // Remove whitespace, new lines, and URL-encoded characters
  str.replace(/\s/g, '').replace(/[^A-Za-z0-9+/=]/g, '');

const fixBase64Padding = (str: string) => {
  while (str.length % 4 !== 0) {
    str += '=';
  }
  return str;
};

const decodeBase64 = (str: string) => {
  const cleanedStr = cleanBase64String(str);
  const fixedStr = fixBase64Padding(cleanedStr);
  try {
    const decodedStr = atob(fixedStr);
    return JSON.parse(decodedStr);
  } catch (e) {
    console.error("Invalid base64 string", e);
    return null;
  }
};

const OffersPage = () => {
  const location = useLocation();

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setProgressBarPercentage(40));
    dispatch(setFlowType('web'));
  }, []);

  useEffect(() => {
    // Preuzimanje query parametara
    const params = new URLSearchParams(location.search);
    const base64Param = params.get('param');

    if (base64Param) {
      // Dekodiranje base64 stringa
      const decoded = decodeBase64(base64Param);

      // Spremanje podataka u Redux
      if (decoded !== null) {
        const { birthDate, email, phoneNumber } = decoded;
        const data: CustomerInterface = {
          birthDate,
          email,
          phoneNumber,
          age: dayjs().diff(dayjs(birthDate), 'year')
        }
        dispatch(setCustomer(data));
      } else {
        dispatch(setCustomer({ birthDate: undefined, age: undefined, email: undefined, phoneNumber: undefined, countryCode: undefined }));
      }
    }
  }, [dispatch, location.search]);

  const { bListToggle, oneTimeOffersToggle } = useAppSelector((store: RootState) => store.model.offer);

  const [bList, setBList] = useState(bListToggle);
  const [oneTime, setOneTime] = useState(oneTimeOffersToggle);

  const navigate = useNavigate();

  const birthDate = useAppSelector((store: RootState) => store.model.customer.data.birthDate) as string;

  const { loading, error, data } = useHealthQuery({
    variables: { birthDate },
    skip: !birthDate,
  });

  useEffect(() => {
    if (!birthDate && location.search === '') {
      navigate(AppRoutes.CUSTOMER.fullPath);
    }
  }, [birthDate, location.search, navigate]);

  if (loading) return <Loader page />;
  if (error) navigate(AppRoutes.CUSTOMER.fullPath);

  // Destrukturiranje podataka
  const { health } = data || {};
  const { webCalculationsSupplementary } = health || {};

  if (!webCalculationsSupplementary) return null;

  // Provjera da li treba filtrirati podatke
  const filteredOffers = bList ?
    webCalculationsSupplementary?.filter((offer: any) =>
      offer.includedInPrice.some((coverage: any) => coverage.label === "B lista lijekova")
    ) :
    webCalculationsSupplementary?.filter((offer: any) =>
      !offer.includedInPrice.some((coverage: any) => coverage.label === "B lista lijekova")
    )

  // Sortiranje podataka po cijeni
  const sortedOffers = filteredOffers.slice().sort((a: any, b: any) => {
    if (oneTime) {
      return a.price.totalOneTimePayment - b.price.totalOneTimePayment;
    }
    return a.price.totalWithInstallments - b.price.totalWithInstallments;
  });

  const onBListToggleChange = (checked: boolean) => {
    setBList(checked);
    dispatch(setBListToggle(checked));
  };

  const onOneTimeToggleChange = (checked: boolean) => {
    setOneTime(checked);
    dispatch(setOneTimeOffersToggle(checked));
  };

  return (
    <Flex vertical gap={24}>
      <Text type="xLarge" bold>{i18n.t('pages.offersPage.title')}</Text>
      <FlexStyle vertical>
        <FilterCard title={i18n.t('pages.offersPage.bListFilter')} description={i18n.t('pages.offersPage.bListFilterDescription')} onToggleChange={(checked) => onBListToggleChange(checked)} checked={bList} />
        <FilterCard title={i18n.t('pages.offersPage.oneTimeFilter')} description={i18n.t('pages.offersPage.oneTimeFilterDescription')} onToggleChange={(checked) => onOneTimeToggleChange(checked)} checked={oneTime} />
      </FlexStyle>
      <Divider dashed />
      {sortedOffers?.map((offer: any) => (
        <OfferCard key={offer.productCode} offer={offer} oneTimeToggle={oneTime} bListToggle={bList} />
      ))}
      {/* {!bList && sortedOffers.find((o: any) => o.price.totalWithInstallments > 111.48) && <OfferInfoCard priceOneTime={111.48} priceInstallments={9.29} oneTimeToggle={oneTime} />} */}
      <SendOffersToEmailCard />
      <RequestAgentCard onButtonClick={() => dispatch(openCallMeModal())} />
    </Flex>
  );
}

export default OffersPage;
